import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AttendanceService } from '../../services/attendance.service';

@Component({
  selector: 'app-clock-in-out',
  templateUrl: './clock-in-out.component.html',
  styleUrls: ['./clock-in-out.component.scss']
})
export class ClockInOutComponent implements OnInit, AfterViewInit {
  clockForm: FormGroup;
  loading = false;
  lastAction: any = null;
  photoDataUrl: string | null = null;
  videoStream: MediaStream | null = null;
  videoReady = false;
  cameraActive = true;
  clientIp: string = '';
  deviceName: string = '';
  @ViewChild('videoRef') videoElement!: ElementRef<HTMLVideoElement>;

  constructor(
    private attendanceService: AttendanceService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private router: Router
  ) {
    this.clockForm = this.fb.group({
      fingerprintId: ['', [Validators.required, Validators.minLength(3)]],
      deviceId: [''],
      location: [''],
      notes: ['']
    });
  }

  ngOnInit(): void {
    this.getClientInfo();
    // Auto-focus on fingerprint ID field
    setTimeout(() => {
      const fingerprintInput = document.getElementById('fingerprintId') as HTMLInputElement;
      if (fingerprintInput) {
        fingerprintInput.focus();
      }
    }, 100);
  }

  private async getClientInfo(): Promise<void> {
    try {
      // Get client IP address
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      this.clientIp = data.ip;
      
      // Get meaningful device information
      this.deviceName = this.getDeviceInfo();
      
      // Try to get friendly device name from server
      try {
        const deviceResponse = await fetch(`/api/Devices/resolve/${this.clientIp}`);
        if (deviceResponse.ok) {
          const deviceData = await deviceResponse.json();
          if (deviceData.friendlyName && deviceData.friendlyName !== this.clientIp) {
            this.deviceName = `${this.deviceName} - ${deviceData.friendlyName}`;
          }
        }
      } catch (deviceError) {
        console.log('Could not resolve device name, using default');
      }
      
      // Update form with auto-populated values
      this.clockForm.patchValue({
        deviceId: this.clientIp,
        location: this.deviceName
      });
    } catch (error) {
      console.error('Error getting client info:', error);
      // Fallback values
      this.clientIp = 'Unknown IP';
      this.deviceName = this.getDeviceInfo();
      this.clockForm.patchValue({
        deviceId: this.clientIp,
        location: this.deviceName
      });
    }
  }

  private getDeviceInfo(): string {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    
    // Detect operating system
    let os = 'Unknown OS';
    if (userAgent.includes('Windows')) {
      if (userAgent.includes('Windows NT 10.0')) os = 'Windows 10';
      else if (userAgent.includes('Windows NT 6.3')) os = 'Windows 8.1';
      else if (userAgent.includes('Windows NT 6.2')) os = 'Windows 8';
      else if (userAgent.includes('Windows NT 6.1')) os = 'Windows 7';
      else os = 'Windows';
    } else if (userAgent.includes('Mac')) {
      os = 'macOS';
    } else if (userAgent.includes('Linux')) {
      os = 'Linux';
    } else if (userAgent.includes('Android')) {
      os = 'Android';
    } else if (userAgent.includes('iOS')) {
      os = 'iOS';
    }
    
    // Detect browser
    let browser = 'Unknown Browser';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    else if (userAgent.includes('Opera')) browser = 'Opera';
    
    // Get screen resolution
    const screenInfo = `${screen.width}x${screen.height}`;
    
    // Detect device type
    let deviceType = 'Desktop';
    if (userAgent.includes('Mobile')) deviceType = 'Mobile';
    else if (userAgent.includes('Tablet')) deviceType = 'Tablet';
    else if (screen.width < 768) deviceType = 'Mobile';
    else if (screen.width < 1024) deviceType = 'Tablet';
    
    // Get additional device info
    const deviceInfo = this.getAdditionalDeviceInfo();
    
    // Create meaningful device identifier
    let deviceIdentifier = `${os} - ${browser} (${screenInfo})`;
    
    // Add device type
    deviceIdentifier += ` [${deviceType}]`;
    
    // Add additional device info if available
    if (deviceInfo) {
      deviceIdentifier += ` - ${deviceInfo}`;
    }
    
    return deviceIdentifier;
  }

  private getAdditionalDeviceInfo(): string {
    const userAgent = navigator.userAgent;
    const additionalInfo: string[] = [];
    
    // Try to get device model information
    if (userAgent.includes('iPhone')) {
      additionalInfo.push('iPhone');
    } else if (userAgent.includes('iPad')) {
      additionalInfo.push('iPad');
    } else if (userAgent.includes('Android')) {
      // Try to extract Android device info
      const androidMatch = userAgent.match(/Android.*?; (.*?) Build/);
      if (androidMatch && androidMatch[1]) {
        additionalInfo.push(androidMatch[1].trim());
      }
    } else if (userAgent.includes('Macintosh')) {
      // For Mac, try to get more specific info
      if (userAgent.includes('Intel Mac')) {
        additionalInfo.push('Intel Mac');
      } else if (userAgent.includes('Apple Silicon')) {
        additionalInfo.push('Apple Silicon Mac');
      }
    } else if (userAgent.includes('Windows')) {
      // For Windows, try to get architecture
      if (userAgent.includes('Win64')) {
        additionalInfo.push('x64');
      } else if (userAgent.includes('Win32')) {
        additionalInfo.push('x86');
      }
    }
    
    // Get timezone for location context
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone && timezone !== 'UTC') {
        additionalInfo.push(timezone);
      }
    } catch (e) {
      // Ignore timezone errors
    }
    
    // Get language for additional context
    const language = navigator.language;
    if (language && language !== 'en-US') {
      additionalInfo.push(`Lang: ${language}`);
    }
    
    return additionalInfo.length > 0 ? additionalInfo.join(', ') : '';
  }

  ngAfterViewInit() {
    this.startCamera();
  }

  onSubmit(): void {
    if (this.clockForm.valid) {
      this.loading = true;
      const formValue = this.clockForm.value;

      if (!this.photoDataUrl) {
        alert('Please capture a photo before clocking in/out.');
        this.loading = false;
        return;
      }

      const dto = {
        ...formValue,
        photo: this.photoDataUrl
      };

      this.attendanceService.clockInOut(dto).subscribe({
        next: (response) => {
          this.lastAction = {
            ...response,
            timestamp: new Date(),
            fingerprintId: formValue.fingerprintId
          };
          
          this.snackBar.open(
            `Successfully recorded clock ${response.message.includes('in') ? 'in' : 'out'}`, 
            'Close', 
            { duration: 3000 }
          );
          
          // Navigate back to dashboard after a short delay
          setTimeout(() => {
            this.router.navigate(['/attendance']);
          }, 1500);
          
          this.loading = false;
        },
        error: (error) => {
          console.error('Error recording clock in/out:', error);
          this.snackBar.open(
            error.error?.message || 'Error recording attendance', 
            'Close', 
            { duration: 5000 }
          );
          this.loading = false;
        }
      });
    }
  }

  getCurrentTime(): string {
    return new Date().toLocaleTimeString();
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString();
  }

  startCamera() {
    navigator.mediaDevices.getUserMedia({ video: true }).then(stream => {
      this.videoStream = stream;
      const video = this.videoElement?.nativeElement;
      if (video) {
        video.srcObject = stream;
        video.oncanplay = () => {
          this.ngZone.run(() => {
            this.videoReady = true;
          });
        };
        video.play();
      }
    });
  }

  capturePhoto() {
    if (!this.videoReady) return;
    const video = this.videoElement?.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg');
      this.ngZone.run(() => {
        this.photoDataUrl = dataUrl;
        this.videoReady = false;
      });
    }
    // Stop camera
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
    }
  }

  resetPhoto() {
    this.ngZone.run(() => {
      this.photoDataUrl = null;
    });
    this.startCamera();
  }

  clockInOut() {
    if (!this.photoDataUrl) {
      alert('Please capture a photo before clocking in/out.');
      this.loading = false;
      return;
    }
    this.loading = true;
    const formValue = this.clockForm.value;
    const dto = {
      ...formValue,
      photo: this.photoDataUrl
    };
    this.attendanceService.clockInOut(dto).subscribe({
      next: (response) => {
        this.lastAction = {
          ...response,
          timestamp: new Date()
        };
        this.loading = false;
        this.ngZone.run(() => {
          this.cameraActive = false;
          this.videoReady = false;
        });
        
        // Show success message
        this.snackBar.open('Clock in/out successful!', 'Close', { duration: 3000 });
        
        // Navigate back to dashboard after a short delay
        setTimeout(() => {
          this.router.navigate(['/attendance']);
        }, 1500);
      },
      error: (error) => {
        this.loading = false;
        this.cdRef.detectChanges();
        this.snackBar.open('Clock in/out failed: ' + (error.error?.message || error.message), 'Close', { duration: 3000 });
      }
    });
  }
} 